import { Controller } from "@hotwired/stimulus"
import mapboxgl from "mapbox-gl"
import { MAP_STYLE, MAP_CONFIG } from "../custom/map_style"

// Connects to data-controller="map-picker"
export default class extends Controller {
  static targets = ["map", "latitude", "longitude", "submit", "coordinateError"]

  static values = {
    token:        String,
    latitude:     { type: Number, default: 51.505 },
    longitude:    { type: Number, default: -0.09 },
    sessionToken: String
  }

  connect() {
    if (!this.tokenValue) {
      console.error("map-picker: Mapbox token is required")
      return
    }

    mapboxgl.accessToken = this.tokenValue

    this.map = new mapboxgl.Map({
      container:   this.mapTarget,
      style:       MAP_STYLE,
      config:      MAP_CONFIG,
      center:      [this.longitudeValue, this.latitudeValue],
      zoom:        15,
      interactive: true
    })

    this.map.addControl(new mapboxgl.NavigationControl(), "top-right")

    // Orange-600: the map-marker colour, matching the sites map's pins
    // (DESIGN.md, TASK-202).
    this.marker = new mapboxgl.Marker({ draggable: true, color: "#E05E0B" })
      .setLngLat([this.longitudeValue, this.latitudeValue])
      .addTo(this.map)

    this.marker.on("dragend", () => this.syncCoordinates())

    this.map.on("click", (e) => {
      this.marker.setLngLat(e.lngLat)
      this.syncCoordinates()
    })

    // Only normalise the fields when they already hold coordinates
    // (server-rendered pickers). Writing the default map centre into empty
    // fields would silently save London for forms the user never touched.
    if (this.hasCoordinates()) this.syncCoordinates()
  }

  hasCoordinates() {
    return this.latitudeTarget.value !== "" && this.longitudeTarget.value !== ""
  }

  // Re-fit the map after its container becomes visible (e.g. a stepper
  // revealing the map step) and centre on coordinates captured earlier —
  // mapbox-gl renders 0×0 inside hidden containers.
  refresh() {
    if (!this.map) return
    requestAnimationFrame(() => {
      this.map.resize()
      const lat = parseFloat(this.latitudeTarget.value)
      const lng = parseFloat(this.longitudeTarget.value)
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        this.marker.setLngLat([lng, lat])
        this.map.jumpTo({ center: [lng, lat], zoom: 15 })
      }
    })
  }

  disconnect() {
    if (this.map) {
      this.map.remove()
      this.map = null
    }
  }

  // Called when an address is selected from the autocomplete
  async onAddressSelect(event) {
    const { id } = event.detail
    if (!id) return

    try {
      const sessionToken = this.sessionTokenValue || crypto.randomUUID()
      const response = await fetch(`/addresses/search/${encodeURIComponent(id)}?session_token=${sessionToken}`, {
        headers: { "Accept": "application/json", "X-Requested-With": "XMLHttpRequest" }
      })

      if (!response.ok) return

      const address = await response.json()
      const lat = parseFloat(address.latitude)
      const lng = parseFloat(address.longitude)

      if (isNaN(lat) || isNaN(lng)) {
        if (this.hasCoordinateErrorTarget) this.coordinateErrorTarget.classList.remove("hidden")
        return
      }

      if (this.hasCoordinateErrorTarget) this.coordinateErrorTarget.classList.add("hidden")

      this.map.flyTo({ center: [lng, lat], zoom: 15, essential: true })
      this.marker.setLngLat([lng, lat])
      this.setCoordinates(lat, lng)
    } catch (error) {
      console.error("map-picker: Failed to fetch address coordinates", error)
    }
  }

  syncCoordinates() {
    const { lat, lng } = this.marker.getLngLat()
    this.setCoordinates(lat, lng)
  }

  setCoordinates(lat, lng) {
    if (this.hasLatitudeTarget)  this.latitudeTarget.value  = lat
    if (this.hasLongitudeTarget) this.longitudeTarget.value = lng
  }
}
